/**
 * Shared pagination response normalizer for v3 list endpoints.
 * Single responsibility: map various API shapes to { data, pagination: { nextCursor, totalCount } }.
 */

/**
 * Extracts list array from API payload (data, results, or []).
 * @param {Object} payload
 * @returns {Array}
 */
export function extractListFromPayload(payload) {
  return Array.isArray(payload?.data)
    ? payload.data
    : payload?.data ?? payload?.results ?? [];
}

/**
 * Extracts pagination meta from API payload.
 * @param {Object} payload
 * @returns {{ currentPage: number, hasNextPage: boolean, totalItems: number }}
 */
export function extractPaginationMeta(payload) {
  const pagination =
    payload?.meta?.pagination ?? payload?.meta ?? payload?.pagination ?? {};
  const currentPage = pagination.currentPage ?? pagination.current_page ?? 1;
  const hasNextPage = Boolean(
    pagination.hasNextPage ?? pagination.has_next_page,
  );
  const totalItems =
    pagination.totalItems ?? pagination.total_items ?? pagination.total;
  return { currentPage, hasNextPage, totalItems };
}

/**
 * Normalizes paginated API response to a consistent shape for UI (infinite list, "Load more").
 *
 * @param {Object} payload - Raw API response (e.g. { data: [], meta: { pagination: {} } })
 * @param {Object} options - { pageSize: number, maxPageSize?: number, sliceToPageSize?: boolean }
 * @returns {{ data: Array, pagination: { nextCursor?: number, totalCount: number } }}
 */
export function normalizePaginatedResponse(payload, options = {}) {
  const {
    pageSize = 10,
    maxPageSize,
    sliceToPageSize = false,
  } = options;

  const list = extractListFromPayload(payload);
  const { currentPage, hasNextPage, totalItems } = extractPaginationMeta(payload);
  const hasMetaPagination = payload?.meta?.pagination != null;

  const effectivePageSize = Math.min(
    pageSize,
    maxPageSize ?? pageSize,
  );
  const totalCount =
    totalItems != null && totalItems > 0 ? totalItems : list.length;
  const hasMoreFromData = list.length > effectivePageSize;
  const hasMore = hasMetaPagination ? hasNextPage : hasMoreFromData;
  const nextCursor = hasMore ? currentPage + 1 : undefined;

  const data =
    sliceToPageSize && list.length > effectivePageSize
      ? list.slice(0, effectivePageSize)
      : list;

  return {
    data,
    pagination: { nextCursor, totalCount },
  };
}
